(() => {
  const config = window.father25GiftTowelConfig;

  if (
    !config ||
    !Number.isInteger(config.variantId) ||
    !Number.isInteger(config.giftStartsAt) ||
    !Number.isInteger(config.saleStartsAt) ||
    !Number.isInteger(config.saleEndsAt) ||
    !Number.isInteger(config.saleThresholdCents) ||
    !Number.isInteger(config.evergreenThresholdCents)
  ) {
    return;
  }

  const CART_MUTATION_RE = /\/cart\/(?:add|change|update|clear)\.js$/;
  const removedStorageKey = 'father25GiftTowelRemoved';
  const unavailableStorageKey = 'father25GiftTowelUnavailable';

  let isSyncing = false;
  let needsSync = false;
  let previousHadGift = Boolean(document.querySelector('[data-father25-gift-line]'));

  const getSessionStorage = (key) => {
    try {
      return window.sessionStorage.getItem(key) === 'true';
    } catch (_error) {
      return false;
    }
  };

  const setSessionStorage = (key, value) => {
    try {
      if (value) {
        window.sessionStorage.setItem(key, 'true');
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (_error) {
      // Storage can be unavailable in private browsing modes.
    }
  };

  const removedKeyForCart = (cart) => `${removedStorageKey}:${cart.token || 'unknown'}`;

  const getRemovedForCart = (cart) => {
    try {
      return window.localStorage.getItem(removedKeyForCart(cart)) === 'true';
    } catch (_error) {
      return getSessionStorage(removedStorageKey);
    }
  };

  const setRemovedForCart = (cart, value) => {
    try {
      if (value) {
        window.localStorage.setItem(removedKeyForCart(cart), 'true');
      } else {
        window.localStorage.removeItem(removedKeyForCart(cart));
      }
    } catch (_error) {
      setSessionStorage(removedStorageKey, value);
    }
  };

  const requestPath = (input) => {
    try {
      const rawUrl = typeof input === 'string' ? input : input.url;
      return new URL(rawUrl, window.location.origin).pathname;
    } catch (_error) {
      return '';
    }
  };

  const isCartMutation = (input) => CART_MUTATION_RE.test(requestPath(input));

  const currentTimestamp = () => Math.floor(Date.now() / 1000);

  const isGiftPromoActive = () => {
    const now = currentTimestamp();

    return Boolean(config.active) && now >= config.giftStartsAt;
  };

  const isSaleActive = () => {
    const now = Math.floor(Date.now() / 1000);

    return now >= config.saleStartsAt && now <= config.saleEndsAt;
  };

  const currentThresholdCents = () =>
    isSaleActive() ? config.saleThresholdCents : config.evergreenThresholdCents;

  const fetchCart = () =>
    fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    }).then((response) => response.json());

  const isPromoGiftLine = (item) =>
    Number(item.id) === config.variantId &&
    item.properties &&
    item.properties[config.giftPropertyName] === config.giftPropertyValue &&
    item.properties[config.promoPropertyName] === config.promoPropertyValue;

  const isExcludedTowelLine = (item) => {
    const excludedVariantIds = config.excludedVariantIds || [config.variantId];
    const productTitle = item.product_title || item.title || '';

    return excludedVariantIds.includes(Number(item.id)) || /golf towel/i.test(productTitle);
  };

  const isQualifyingLine = (item) => {
    const vendor = item.vendor || item.product_vendor || '';

    return vendor === config.qualifyingVendor && !isPromoGiftLine(item) && !isExcludedTowelLine(item);
  };

  const qualifyingSubtotal = (cart) =>
    (cart.items || []).reduce((subtotal, item) => {
      if (!isQualifyingLine(item)) {
        return subtotal;
      }

      return subtotal + Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
    }, 0);

  const dispatchCartUpdate = (cart) => {
    document.dispatchEvent(
      new CustomEvent('updateCart', {
        detail: {
          cart,
          data: {
            father25GiftTowel: true
          }
        }
      })
    );
  };

  const postCart = (path, body) =>
    fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        ...body,
        sections: 'mini-cart'
      })
    }).then((response) => response.json());

  const addGift = async () => {
    const addResponse = await postCart('/cart/add.js', {
      items: [
        {
          id: config.variantId,
          quantity: 1,
          properties: {
            [config.giftPropertyName]: config.giftPropertyValue,
            [config.promoPropertyName]: config.promoPropertyValue
          }
        }
      ]
    });

    if (addResponse.status === 422) {
      setSessionStorage(unavailableStorageKey, true);
      return null;
    }

    const cart = await fetchCart();
    cart.sections = addResponse.sections;
    dispatchCartUpdate(cart);
    return cart;
  };

  const changeLine = async (key, quantity) => {
    const cart = await postCart('/cart/change.js', {
      id: key,
      quantity
    });

    if (cart.status !== 422) {
      dispatchCartUpdate(cart);
    }

    return cart;
  };

  const normalizeGiftLines = async (giftLines) => {
    const [firstGift, ...extraGifts] = giftLines;

    if (!firstGift) {
      return;
    }

    if (firstGift.quantity !== 1) {
      await changeLine(firstGift.key, 1);
    }

    for (const extraGift of extraGifts) {
      await changeLine(extraGift.key, 0);
    }
  };

  const removeGiftLines = async (giftLines) => {
    for (const giftLine of giftLines) {
      await changeLine(giftLine.key, 0);
    }
  };

  const syncGift = async () => {
    if (isSyncing) {
      needsSync = true;
      return;
    }

    isSyncing = true;

    try {
      const cart = await fetchCart();
      const giftLines = (cart.items || []).filter(isPromoGiftLine);
      const hasGift = giftLines.length > 0;
      const isEligible = isGiftPromoActive() && qualifyingSubtotal(cart) >= currentThresholdCents();

      if (previousHadGift && !hasGift && isEligible) {
        setRemovedForCart(cart, true);
      }

      previousHadGift = hasGift;

      if (!isGiftPromoActive() || !isEligible) {
        if (hasGift) {
          await removeGiftLines(giftLines);
          previousHadGift = false;
        }

        return;
      }

      if (hasGift) {
        await normalizeGiftLines(giftLines);
        previousHadGift = true;
        return;
      }

      if (!getRemovedForCart(cart) && !getSessionStorage(unavailableStorageKey)) {
        const updatedCart = await addGift();
        previousHadGift = Boolean(updatedCart && (updatedCart.items || []).some(isPromoGiftLine));
      }
    } catch (error) {
      console.error('Father25 gift towel sync failed:', error);
    } finally {
      isSyncing = false;

      if (needsSync) {
        needsSync = false;
        window.setTimeout(syncGift, 150);
      }
    }
  };

  const scheduleSync = () => {
    window.setTimeout(syncGift, 100);
  };

  const originalFetch = window.fetch.bind(window);

  window.fetch = (...args) => {
    const result = originalFetch(...args);

    if (isCartMutation(args[0])) {
      result.finally(scheduleSync);
    }

    return result;
  };

  document.addEventListener('updateCart', scheduleSync);
  document.addEventListener('mini-cart:open', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, {once: true});
  } else {
    scheduleSync();
  }
})();
